import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ToastProvider } from "./components/ui";
import { AuthProvider } from "./lib/auth";
import "./styles.css";

const queryClient: QueryClient = new QueryClient({
  // Anything saved anywhere can change what another screen shows — a stage
  // move alters the board, the job, the lists and the reports at once. Rather
  // than each save remembering every screen that might care (and one of them
  // forgetting), any successful save marks everything stale, and whatever is
  // on screen refetches straight away. Screens not showing don't refetch.
  mutationCache: new MutationCache({
    // A save that removes the very thing on screen (deleting the open job)
    // says so in `meta` and cleans up for itself — refetching a job that no
    // longer exists would only put a 404 in front of the user.
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.skipGlobalRefresh) return;
      void queryClient.invalidateQueries();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      // Coming back to the tab after someone else changed a job.
      refetchOnWindowFocus: true,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/* One switch for the whole product: with prefers-reduced-motion set,
        every Framer animation becomes instant. */}
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </MotionConfig>
  </StrictMode>,
);
